import { StrictMode } from "react";
// Before anything renders, so the first frame is already in the right language.
import "@/i18n";
import { installDomGuard } from "@/lib/dom-guard";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "@/lib/theme";
import { ApiError } from "@/lib/api";
import { Toaster, toast } from "@/components/Toaster";
import { ImportLinkHost } from "@/components/ImportLinkDialog";
import { errorMessage } from "@/components/ui";
import { router } from "@/router";
import "./index.css";

// Before React touches the DOM — see the file for the browser-translation crash.
installDomGuard();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // A 404 means the row does not exist or is not ours — retrying cannot
      // change either, and three failed round trips just delay the error state.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
      // Returning to a tab after a meeting should not show stale data. The
      // queries that matter are cheap, and `staleTime` still prevents a storm.
      refetchOnWindowFocus: true,
    },
    mutations: {
      /**
       * Every mutation reports its failure somewhere.
       *
       * About fifteen had no `onError` at all, so a failed delete, stop, remove
       * or revoke looked exactly like success. Making this the default means a
       * new mutation is loud by accident rather than silent by accident — call
       * sites that render their own error still can, and simply do not reach
       * here.
       */
      onError: (error) => toast(errorMessage(error)),
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("#root is missing from index.html");

createRoot(container).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* Outside the router so a notice survives a navigation. */}
        <Toaster />
        {/* Same reason: the dialog must outlive whichever page opened it. */}
        <ImportLinkHost />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
